/**
 * Shared application constants. Use these instead of magic strings
 * for routing, query params, and cross-cutting values.
 */

/** Query param used to open the demo/booking modal (e.g. from shared links or marketing). */
export const OPEN_DEMO_QUERY_PARAM = "openDemo";

/** Value for OPEN_DEMO_QUERY_PARAM when the modal should open. */
export const OPEN_DEMO_QUERY_VALUE = "1";

/** Custom event name to open the demo/booking modal from anywhere on the landing page. */
export const OPEN_BOOKING_MODAL_EVENT = "openBookingModal";

/** Route prefixes for pages that require authentication. */
export const PROTECTED_ROUTE_PREFIXES = [
  "/ai-chat",
  "/calendar",
  "/settings",
  "/onboarding",
  "/email-compliance",
] as const;

/**
 * Route prefixes for the in-app dashboard. Not an appearance input: route
 * appearance comes from FORCED_DARK_ROUTE_PREFIXES.
 */
export const DASHBOARD_ROUTE_PREFIXES = [
  "/ai-chat",
  "/calendar",
  "/settings",
  "/onboarding",
  "/email-compliance",
] as const;

/** Appearance policy, separate from authentication. Unknown routes inherit. */
export const FORCED_DARK_ROUTE_PREFIXES = [
  "/",
  "/about",
  "/accept-invite",
  "/auth",
  "/company",
  "/confirm-email",
  "/contact",
  "/faq",
  "/login",
  "/newsroom",
  "/platform",
  "/pricing",
  "/privacy",
  "/product",
  "/register",
  "/request-access",
  "/reset-password",
  "/security",
  "/solutions",
  "/support",
  "/team",
  "/terms",
  "/unsubscribe",
  "/waitlist",
] as const;

/**
 * Also serialized into DOCUMENT_APPEARANCE_BOOTSTRAP as the forced-dark matcher
 * (lib/theme-appearance-policy.ts), so first paint and React share one
 * implementation. Keep it closure-free: no imports and no global checks.
 */
export function matchesRoutePrefix(
  pathname: string | null | undefined,
  prefixes: readonly string[],
): boolean {
  if (!pathname) return false;
  return prefixes.some(
    (p) => pathname === p || (p !== "/" && pathname.startsWith(p + "/")),
  );
}

/** True for landing, marketing, auth, and other non-dashboard routes. */
export function isPublicMarketingRoute(
  pathname: string | null | undefined,
): boolean {
  if (!pathname) return false;
  return !matchesRoutePrefix(pathname, DASHBOARD_ROUTE_PREFIXES);
}

/**
 * True for routes that should be dark for every visitor regardless of stored or
 * system preference (landing, marketing, auth, legal), UI-kit preview frames
 * included. The document bootstrap runs the same matchesRoutePrefix source over
 * FORCED_DARK_ROUTE_PREFIXES, so first paint and hydration agree.
 * Authentication is intentionally separate from appearance; unknown routes
 * inherit preferences.
 */
export function shouldForceDarkRoute(
  pathname: string | null | undefined,
): boolean {
  if (!pathname) return false;
  return matchesRoutePrefix(pathname, FORCED_DARK_ROUTE_PREFIXES);
}
